/**
 * Pure cascading-filter helpers shared by Month / Week / Day views.
 *
 * Legacy `source/js/modules/calendar/calendar.filter.js:237-335` implemented
 * this cascade imperatively inside AngularJS controllers. The React port
 * hoists the logic into small pure functions so both the adapter layer
 * (narrowing dropdown options) and the container layer (dropping orphan ids
 * from `filters.*` after a parent change) can reuse the exact same rules:
 *
 *   Org → Location          by `organizationUnique`
 *   Org → Pilot             by `organizationUnique`
 *   Org + Location → Resource      (by org first, then by `locationID`)
 *   Org + Location → Instructor    (same rules as Resource)
 *
 * Fail-open contract: an EMPTY `orgs`/`locations` array means "no filter" —
 * the caller sees the full input list. This matches the legacy behaviour
 * where unchecking every org does not lock the dependent dropdowns.
 *
 * All helpers expect the adapted object shape produced by
 * `utils/monthFilterDataAdapter.js` (i.e. `{ id, organizationUnique,
 * locationID, ... }`). They never mutate their input.
 */

/**
 * Keep items whose `organizationUnique` is in the supplied set.
 * Empty/missing `orgs` → pass-through.
 *
 * @template T
 * @param {T[]} items
 * @param {Array<string|number>|null|undefined} orgs
 * @returns {T[]}
 */
export function filterByOrgs(items, orgs) {
	if (!Array.isArray(items)) return [];
	if (!orgs || orgs.length === 0) return items.filter(Boolean);
	const set = new Set(orgs.map(String));
	return items.filter(i => i && set.has(String(i.organizationUnique)));
}

/**
 * Keep items whose `locationID` is in the supplied set.
 * Empty/missing `locations` → pass-through.
 *
 * @template T
 * @param {T[]} items
 * @param {Array<string|number>|null|undefined} locations
 * @returns {T[]}
 */
export function filterByLocations(items, locations) {
	if (!Array.isArray(items)) return [];
	if (!locations || locations.length === 0) return items.filter(Boolean);
	const set = new Set(locations.map(String));
	return items.filter(i => i && set.has(String(i.locationID)));
}

/** Locations cascade only on org. */
export function cascadeLocations(items, orgs) {
	return filterByOrgs(items, orgs);
}

/** Pilots cascade only on org (legacy parity — no location narrowing). */
export function cascadePilots(items, orgs) {
	return filterByOrgs(items, orgs);
}

/** Resources cascade on org AND location. */
export function cascadeResources(items, orgs, locations) {
	return filterByLocations(filterByOrgs(items, orgs), locations);
}

/** Instructors share the resource cascade rules. */
export function cascadeInstructors(items, orgs, locations) {
	return filterByLocations(filterByOrgs(items, orgs), locations);
}

/**
 * Given a list of currently-selected child ids and the fully-cascaded
 * visible list, return the subset of ids that are still visible. Used by
 * `MonthV2Filter.handleFiltersChange` to drop orphans when the user changes
 * a parent filter (e.g. removes an org that had selected locations).
 *
 * Idempotent: an already-clean selection is returned unchanged (same array
 * reference when possible) so `useCallback` consumers don't over-dispatch.
 *
 * @param {Array<string|number>} selectedIds
 * @param {Array<{id: string|number}>} visibleItems
 * @returns {Array<string>}  Always strings — matches slice state shape.
 */
export function pruneOrphanIds(selectedIds, visibleItems) {
	if (!Array.isArray(selectedIds) || selectedIds.length === 0) return [];
	const visibleSet = new Set((visibleItems || []).map(i => String(i && i.id)));
	const out = [];
	for (const id of selectedIds) {
		const key = String(id);
		if (visibleSet.has(key)) out.push(key);
	}
	return out;
}
